import React, { useEffect, useMemo, useState } from "react";
import { ArrowRightLeft, Edit3, Eye, KeyRound, Link as LinkIcon, Mail, Phone, Plus, RefreshCw, Search, User, X } from "lucide-react";
import API from "../services/api";
import AdminLayout from "./AdminLayout";

const makeFourDigitCode = () => String(Math.floor(1000 + Math.random() * 9000));

const emptyAgentForm = () => ({
  name: "",
  phone: "",
  email: "",
  password: "",
  twofa_code: makeFourDigitCode(),
  commission_rate: "0",
  agent_color: "#fff7ed",
  agent_public_name: "",
  agent_whatsapp_number: "",
});

function AdminAgents() {
  const [data, setData] = useState({ totals: {}, agents: [] });
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [form, setForm] = useState(emptyAgentForm);
  const [editForm, setEditForm] = useState({ ...emptyAgentForm(), id: "", twofa_code: "", status: "Active" });
  const [transferForm, setTransferForm] = useState({ user_id: "", agent_id: "" });
  const [agentDetail, setAgentDetail] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  const load = async () => {
    try {
      const [agentRes, userRes] = await Promise.all([API.get("/admin/agents"), API.get("/admin/users")]);
      setData(agentRes.data || { totals: {}, agents: [] });
      setUsers(Array.isArray(userRes.data) ? userRes.data : []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredAgents = useMemo(() => {
    const search = query.trim().toLowerCase();
    return (data.agents || []).filter((item) => {
      if (!search) return true;
      return [item.name, item.phone, item.email, item.referral_code].filter(Boolean).some((value) => String(value).toLowerCase().includes(search));
    });
  }, [data.agents, query]);

  const createAgent = async () => {
    try {
      await API.post("/admin/agents", form);
      setShowCreate(false);
      setForm(emptyAgentForm());
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to create agent");
    }
  };

  const transferClient = async () => {
    try {
      await API.post("/admin/agents/transfer-user", transferForm);
      setShowTransfer(false);
      setTransferForm({ user_id: "", agent_id: "" });
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to transfer client");
    }
  };

  const resetAgentCode = async (agent) => {
    const nextCode = makeFourDigitCode();
    try {
      await API.put(`/admin/agents/${agent.id}`, { twofa_code: nextCode });
      alert(`New 2FA code for ${agent.name}: ${nextCode}`);
      await load();
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to reset agent 2FA");
    }
  };

  const openAgent = async (agent) => {
    try {
      const res = await API.get(`/admin/agents/${agent.id}`);
      setAgentDetail(res.data || null);
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to open agent");
    }
  };

  const openEdit = (agent) => {
    setEditForm({
      id: agent.id,
      name: agent.name || "",
      phone: agent.phone || "",
      email: agent.email || "",
      password: "",
      twofa_code: agent.twofa_code || "",
      commission_rate: String(agent.commission_rate || "0"),
      status: agent.status || "Active",
      agent_color: agent.agent_color || "#fff7ed",
      agent_public_name: agent.agent_public_name || "",
      agent_whatsapp_number: agent.agent_whatsapp_number || "",
    });
    setShowEdit(true);
  };

  const saveAgent = async () => {
    try {
      const payload = {
        name: editForm.name,
        phone: editForm.phone,
        email: editForm.email,
        twofa_code: editForm.twofa_code,
        commission_rate: editForm.commission_rate,
        status: editForm.status,
        agent_color: editForm.agent_color,
        agent_public_name: editForm.agent_public_name,
        agent_whatsapp_number: editForm.agent_whatsapp_number,
      };
      if (editForm.password) payload.password = editForm.password;
      await API.put(`/admin/agents/${editForm.id}`, payload);
      setShowEdit(false);
      await load();
      if (agentDetail?.agent?.id === editForm.id) {
        await openAgent({ id: editForm.id });
      }
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to update agent");
    }
  };

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div>
            <h1 style={title}>Agent Program</h1>
            <p style={subtitle}>Manage affiliate partners and client transfers</p>
          </div>
          <div style={actionRow}>
            <button type="button" style={secondaryButton} onClick={() => setShowTransfer(true)}><ArrowRightLeft size={16} /> Client Transfer</button>
            <button
              type="button"
              style={primaryButton}
              onClick={() => {
                setForm(emptyAgentForm());
                setShowCreate(true);
              }}
            >
              <Plus size={16} /> Add Agent
            </button>
          </div>
        </div>

        <div style={statsGrid}>
          <StatCard label="Agents" value={data.totals?.agents || 0} color="#2563eb" />
          <StatCard label="Active" value={data.totals?.active || 0} color="#16a34a" />
          <StatCard label="Clients" value={data.totals?.clients || 0} color="#2563eb" />
          <StatCard label="Deposits" value={formatMoney(data.totals?.deposits)} color="#16a34a" />
          <StatCard label="Withdrawals" value={formatMoney(data.totals?.withdrawals)} color="#dc2626" />
          <StatCard label="Net DMW" value={formatMoney(data.totals?.dmw)} color="#7c3aed" />
          <StatCard label="Pending" value={formatMoney(data.totals?.pending)} color="#d97706" />
        </div>

        <div style={toolbar}>
          <div style={searchWrap}>
            <Search size={16} color="#94a3b8" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search agents..." style={searchInput} />
          </div>
          <button type="button" style={refreshButton} onClick={load}><RefreshCw size={16} /></button>
        </div>

        <div style={tableWrap}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Agent</th>
                <th style={th}>Code</th>
                <th style={th}>Clients</th>
                <th style={th}>Deposits</th>
                <th style={th}>DMW</th>
                <th style={th}>Rate</th>
                <th style={th}>Commission</th>
                <th style={th}>Pending</th>
                <th style={th}>Status</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgents.map((agent) => (
                <tr key={agent.id} style={agentRow(agent)}>
                  <td style={td}>
                    <strong>{agent.name}</strong>
                    <div style={subText}>{agent.phone || agent.email}</div>
                    {agent.agent_public_name ? <div style={subText}>Website: {agent.agent_public_name}</div> : null}
                  </td>
                  <td style={td}><span style={codePill}>{agent.referral_code || "-"}</span></td>
                  <td style={td}>{agent.clients || 0}</td>
                  <td style={td}>{formatMoney(agent.deposits)}</td>
                  <td style={{ ...td, color: Number(agent.dmw || 0) < 0 ? "#dc2626" : "#16a34a", fontWeight: 800 }}>{formatMoney(agent.dmw)}</td>
                  <td style={td}>{Number(agent.commission_rate || 0)}%</td>
                  <td style={{ ...td, color: "#ea580c", fontWeight: 900 }}>{formatMoney(agent.exact_commission)}</td>
                  <td style={td}>{formatMoney(agent.pending)}</td>
                  <td style={td}><span style={statusPill(agent.status)}>{String(agent.status || "Active").toLowerCase()}</span></td>
                  <td style={td}>
                    <div style={rowActions}>
                      <button type="button" title="View agent" style={iconActionButton} onClick={() => openAgent(agent)}><Eye size={15} /></button>
                      <button type="button" title="Edit agent" style={iconActionButton} onClick={() => openEdit(agent)}><Edit3 size={15} /></button>
                      <button type="button" style={miniButton} onClick={() => resetAgentCode(agent)}><KeyRound size={14} /> Reset 2FA</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showCreate && (
          <Modal title="Add Agent" onClose={() => setShowCreate(false)} onSave={createAgent}>
            <Field label="Name"><input style={input} value={form.name} onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))} /></Field>
            <Field label="Website Display Name"><input style={input} placeholder="Name users will see" value={form.agent_public_name} onChange={(e) => setForm((c) => ({ ...c, agent_public_name: e.target.value }))} /></Field>
            <Field label="Phone"><input style={input} value={form.phone} onChange={(e) => setForm((c) => ({ ...c, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))} /></Field>
            <Field label="Agent WhatsApp Number"><input style={input} placeholder="For user's personal agent contact" value={form.agent_whatsapp_number} onChange={(e) => setForm((c) => ({ ...c, agent_whatsapp_number: e.target.value.replace(/\D/g, "").slice(0, 15) }))} /></Field>
            <Field label="Email"><input style={input} value={form.email} onChange={(e) => setForm((c) => ({ ...c, email: e.target.value }))} /></Field>
            <Field label="Password"><input style={input} value={form.password} onChange={(e) => setForm((c) => ({ ...c, password: e.target.value }))} /></Field>
            <Field label="2FA Code">
              <div style={inlineField}>
                <input
                  style={{ ...input, flex: 1 }}
                  value={form.twofa_code}
                  onChange={(e) => setForm((c) => ({ ...c, twofa_code: e.target.value.replace(/\D/g, "").slice(0, 4) }))}
                />
                <button type="button" style={miniButton} onClick={() => setForm((c) => ({ ...c, twofa_code: makeFourDigitCode() }))}>
                  <RefreshCw size={14} /> Refresh
                </button>
              </div>
            </Field>
            <Field label="Rate %"><input style={input} value={form.commission_rate} onChange={(e) => setForm((c) => ({ ...c, commission_rate: e.target.value }))} /></Field>
            <Field label="Agent Row Color"><input style={colorInput} type="color" value={form.agent_color} onChange={(e) => setForm((c) => ({ ...c, agent_color: e.target.value }))} /></Field>
          </Modal>
        )}

        {showEdit && (
          <Modal title="Edit Agent" onClose={() => setShowEdit(false)} onSave={saveAgent}>
            <Field label="Name"><input style={input} value={editForm.name} onChange={(e) => setEditForm((c) => ({ ...c, name: e.target.value }))} /></Field>
            <Field label="Website Display Name"><input style={input} placeholder="Name users will see" value={editForm.agent_public_name} onChange={(e) => setEditForm((c) => ({ ...c, agent_public_name: e.target.value }))} /></Field>
            <Field label="Phone"><input style={input} value={editForm.phone} onChange={(e) => setEditForm((c) => ({ ...c, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))} /></Field>
            <Field label="Agent WhatsApp Number"><input style={input} placeholder="For user's personal agent contact" value={editForm.agent_whatsapp_number} onChange={(e) => setEditForm((c) => ({ ...c, agent_whatsapp_number: e.target.value.replace(/\D/g, "").slice(0, 15) }))} /></Field>
            <Field label="Email"><input style={input} value={editForm.email} onChange={(e) => setEditForm((c) => ({ ...c, email: e.target.value }))} /></Field>
            <Field label="Password"><input style={input} placeholder="Leave blank to keep same" value={editForm.password} onChange={(e) => setEditForm((c) => ({ ...c, password: e.target.value }))} /></Field>
            <Field label="2FA Code"><input style={input} value={editForm.twofa_code} onChange={(e) => setEditForm((c) => ({ ...c, twofa_code: e.target.value.replace(/\D/g, "").slice(0, 4) }))} /></Field>
            <Field label="Commission %"><input style={input} value={editForm.commission_rate} onChange={(e) => setEditForm((c) => ({ ...c, commission_rate: e.target.value }))} /></Field>
            <Field label="Agent Row Color"><input style={colorInput} type="color" value={editForm.agent_color} onChange={(e) => setEditForm((c) => ({ ...c, agent_color: e.target.value }))} /></Field>
            <Field label="Status">
              <select style={input} value={editForm.status} onChange={(e) => setEditForm((c) => ({ ...c, status: e.target.value }))}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Blocked">Blocked</option>
              </select>
            </Field>
          </Modal>
        )}

        {showTransfer && (
          <Modal title="Transfer Client" onClose={() => setShowTransfer(false)} onSave={transferClient}>
            <Field label="User">
              <select style={input} value={transferForm.user_id} onChange={(e) => setTransferForm((c) => ({ ...c, user_id: e.target.value }))}>
                <option value="">Select user</option>
                {users.map((user) => <option key={user.id} value={user.id}>{user.name} - {user.phone}</option>)}
              </select>
            </Field>
            <Field label="Agent">
              <select style={input} value={transferForm.agent_id} onChange={(e) => setTransferForm((c) => ({ ...c, agent_id: e.target.value }))}>
                <option value="">Direct</option>
                {(data.agents || []).map((agent) => <option key={agent.id} value={agent.id}>{agent.name}</option>)}
              </select>
            </Field>
          </Modal>
        )}

        {agentDetail && (
          <AgentDetailView
            detail={agentDetail}
            onClose={() => setAgentDetail(null)}
            onEdit={() => openEdit(agentDetail.agent)}
          />
        )}
      </section>
    </AdminLayout>
  );
}

function AgentDetailView({ detail, onClose, onEdit }) {
  const agent = detail.agent || {};
  const clients = detail.clients || [];
  const activeClients = clients.filter((client) => String(client.status || "Active") === "Active").length || clients.length;
  return (
    <div style={detailOverlay}>
      <section style={detailPanel}>
        <header style={detailHeader}>
          <div style={agentIdentity}>
            <span style={agentAvatar}>{String(agent.name || "A").slice(0, 1).toUpperCase()}</span>
            <div>
              <div style={agentNameRow}>
                <strong style={detailName}>{agent.name || "Agent"}</strong>
                <span style={statusPill(agent.status)}>{String(agent.status || "Active").toLowerCase()}</span>
              </div>
              <div style={agentMetaRow}>
                <span><Phone size={12} /> {agent.phone || "-"}</span>
                <span><Mail size={12} /> {agent.email || "-"}</span>
                <span><LinkIcon size={12} /> {agent.referral_code || "-"}</span>
              </div>
            </div>
          </div>
          <div style={detailActions}>
            <button type="button" style={plainAction} onClick={onEdit}><Edit3 size={14} /> Edit</button>
            <button type="button" style={closeDetailButton} onClick={onClose}><X size={18} /></button>
          </div>
        </header>

        <div style={detailStats}>
          <DetailStat value={agent.clients || 0} label="Total Clients" foot={`${activeClients} active`} color="#111827" />
          <DetailStat value={formatCompactMoney(agent.deposits)} label="Total Deposits" color="#16a34a" />
          <DetailStat value={formatCompactMoney(agent.withdrawals)} label="Total Withdrawals" color="#dc2626" />
          <DetailStat value={formatCompactMoney(agent.dmw)} label="Net DMW" color={Number(agent.dmw || 0) < 0 ? "#dc2626" : "#16a34a"} />
          <DetailStat value={`${Number(agent.commission_rate || 0)}%`} label="Commission Rate" color="#7c3aed" />
          <DetailStat value={formatCompactMoney(agent.exact_commission)} label="Exact Commission" color="#ea580c" />
        </div>

        <nav style={detailTabs}>
          <button type="button" style={activeDetailTab}><User size={14} /> Overview</button>
          <button type="button" style={detailTab}>Clients <span>{agent.clients || 0}</span></button>
          <button type="button" style={detailTab}>Commissions <span>0</span></button>
        </nav>

        <div style={detailGrid}>
          <section style={detailCard}>
            <h3 style={detailCardTitle}>% Commission Calculator</h3>
            <div style={commissionBox}>
              <Line label="Total Deposits" value={formatMoney(agent.deposits)} />
              <Line label="Total Withdrawals" value={formatMoney(agent.withdrawals)} color="#dc2626" />
              <Line label="Agent User Profit" value={formatMoney(agent.dmw)} color={Number(agent.dmw || 0) < 0 ? "#dc2626" : "#16a34a"} />
              <Line label="Commission Rate" value={`${Number(agent.commission_rate || 0)}%`} color="#7c3aed" />
              <Line label="Exact Commission" value={formatMoney(agent.exact_commission || agent.expected_commission)} color="#ea580c" strong />
            </div>
            <div style={commissionTiles}>
              <MiniMetric label="Total Earned" value={formatMoney(agent.exact_commission || agent.expected_commission)} />
              <MiniMetric label="Pending" value={formatMoney(agent.pending_commission)} color="#ea580c" />
              <MiniMetric label="Paid" value={formatMoney(agent.paid_commission)} color="#16a34a" />
            </div>
          </section>

          <section style={detailCard}>
            <h3 style={detailCardTitle}>Agent Details</h3>
            <Line label="Full Name" value={agent.name || "-"} />
            <Line label="Website Display Name" value={agent.agent_public_name || agent.name || "-"} color="#2563eb" />
            <Line label="Mobile" value={agent.phone || "-"} />
            <Line label="Agent WhatsApp" value={agent.agent_whatsapp_number || "-"} color="#16a34a" />
            <Line label="Email" value={agent.email || "-"} />
            <Line label="Referral Code" value={agent.referral_code || "-"} color="#2563eb" />
            <Line label="Wallet Balance" value={formatMoney(agent.balance)} color="#16a34a" />
            <Line label="Joined" value={formatDate(agent.created_at)} />
            <Line label="Total Bets" value={Number(agent.total_bets || 0).toLocaleString("en-IN")} />
            <Line label="Win Rate" value={`${Number(agent.win_rate || 0).toFixed(1)}%`} />
          </section>
        </div>
      </section>
    </div>
  );
}

function DetailStat({ value, label, foot, color }) {
  return (
    <div style={detailStat}>
      <strong style={{ ...detailStatValue, color }}>{value}</strong>
      <span>{label}</span>
      {foot ? <small>{foot}</small> : null}
    </div>
  );
}

function Line({ label, value, color = "#111827", strong = false }) {
  return (
    <div style={detailLine}>
      <span>{label}</span>
      <strong style={{ color, fontWeight: strong ? 900 : 800 }}>{value}</strong>
    </div>
  );
}

function MiniMetric({ label, value, color = "#111827" }) {
  return <div style={miniMetric}><strong style={{ color }}>{value}</strong><span>{label}</span></div>;
}

function Modal({ title, children, onClose, onSave }) {
  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={modalHeader}>
          <strong>{title}</strong>
          <button type="button" style={iconButton} onClick={onClose}>x</button>
        </div>
        <div style={{ display: "grid", gap: 12 }}>{children}</div>
        <div style={modalFooter}>
          <button type="button" style={secondaryModalButton} onClick={onClose}>Cancel</button>
          <button type="button" style={primaryModalButton} onClick={onSave}>Save</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label style={field}><span style={fieldLabel}>{label}</span>{children}</label>;
}

function StatCard({ label, value, color }) {
  return <div style={statCard}><div style={{ ...statValue, color }}>{value}</div><div style={statLabel}>{label}</div></div>;
}

function formatMoney(value) {
  return `Rs ${Number(value || 0).toFixed(2)}`;
}

function formatCompactMoney(value) {
  const amount = Number(value || 0);
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(amount);
  if (abs >= 10000000) return `${sign}Rs ${(abs / 10000000).toFixed(2)}Cr`;
  if (abs >= 100000) return `${sign}Rs ${(abs / 100000).toFixed(2)}L`;
  if (abs >= 1000) return `${sign}Rs ${(abs / 1000).toFixed(2)}K`;
  return `${sign}Rs ${abs.toFixed(0)}`;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

const statusPill = (status) => ({
  display: "inline-flex",
  alignItems: "center",
  minHeight: 28,
  padding: "0 10px",
  borderRadius: 999,
  background: String(status || "").toLowerCase() === "active" ? "#dcfce7" : "#fee2e2",
  color: String(status || "").toLowerCase() === "active" ? "#15803d" : "#dc2626",
  fontWeight: 800,
  textTransform: "lowercase",
});

const agentRow = (agent = {}) => {
  const color = String(agent.agent_color || "").trim();
  if (!color) return {};
  return {
    background: `linear-gradient(90deg, ${color} 0%, rgba(255,255,255,.96) 30%, #ffffff 100%)`,
    boxShadow: `inset 4px 0 0 ${color}`,
  };
};

const page = { display: "grid", gap: 18 };
const header = { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 };
const title = { margin: 0, fontSize: 18, fontWeight: 900 };
const subtitle = { margin: "4px 0 0", color: "#64748b" };
const actionRow = { display: "flex", gap: 10 };
const primaryButton = { minHeight: 42, padding: "0 16px", border: 0, borderRadius: 12, background: "#2563eb", color: "#fff", fontWeight: 800, display: "flex", alignItems: "center", gap: 8 };
const secondaryButton = { ...primaryButton, background: "#f59e0b" };
const statsGrid = { display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 12 };
const statCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 };
const statValue = { fontSize: 18, fontWeight: 900 };
const statLabel = { marginTop: 8, color: "#64748b", fontSize: 12, fontWeight: 700 };
const toolbar = { display: "flex", gap: 10 };
const searchWrap = { flex: 1, minHeight: 42, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, display: "flex", alignItems: "center", gap: 10, padding: "0 14px" };
const searchInput = { width: "100%", border: 0, outline: 0 };
const refreshButton = { width: 42, minWidth: 42, border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff", display: "grid", placeItems: "center" };
const tableWrap = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, overflow: "auto" };
const table = { width: "100%", borderCollapse: "collapse" };
const th = { padding: 14, textAlign: "left", borderBottom: "1px solid #e2e8f0", color: "#64748b", fontSize: 12 };
const td = { padding: 14, borderBottom: "1px solid #f1f5f9" };
const subText = { marginTop: 4, color: "#64748b", fontSize: 13 };
const codePill = { display: "inline-flex", alignItems: "center", minHeight: 28, padding: "0 10px", borderRadius: 10, background: "#eff6ff", color: "#2563eb", fontWeight: 800 };
const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "grid", placeItems: "center", zIndex: 999 };
const modal = { width: 460, maxWidth: "92vw", maxHeight: "90vh", overflow: "auto", background: "#fff", borderRadius: 20, padding: 20, display: "grid", gap: 16 };
const modalHeader = { display: "flex", alignItems: "center", justifyContent: "space-between" };
const iconButton = { width: 32, height: 32, borderRadius: 10, border: 0, background: "#f8fafc" };
const modalFooter = { display: "flex", justifyContent: "flex-end", gap: 10 };
const secondaryModalButton = { minHeight: 42, padding: "0 16px", borderRadius: 12, border: "1px solid #dbe4f0", background: "#fff", fontWeight: 800 };
const primaryModalButton = { minHeight: 42, padding: "0 16px", borderRadius: 12, border: 0, background: "#2563eb", color: "#fff", fontWeight: 800 };
const field = { display: "grid", gap: 8 };
const fieldLabel = { color: "#475569", fontWeight: 700, fontSize: 13 };
const input = { minHeight: 44, border: "1px solid #dbe4f0", borderRadius: 12, padding: "0 12px" };
const colorInput = { ...input, width: "100%", padding: "4px 8px", background: "#fff", cursor: "pointer" };
const inlineField = { display: "flex", gap: 10, alignItems: "center" };
const miniButton = { minHeight: 44, padding: "0 14px", borderRadius: 12, border: "1px solid #bfdbfe", background: "#eff6ff", color: "#2563eb", fontWeight: 800, display: "flex", alignItems: "center", gap: 6 };
const rowActions = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const iconActionButton = { width: 38, height: 38, borderRadius: 10, border: "1px solid #dbeafe", background: "#fff", color: "#2563eb", display: "grid", placeItems: "center", cursor: "pointer" };
const detailOverlay = { position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15,23,42,0.5)", display: "grid", placeItems: "center", padding: 18 };
const detailPanel = { width: "min(920px, 96vw)", maxHeight: "92vh", overflow: "auto", background: "#f8fafc", borderRadius: 12, boxShadow: "0 24px 70px rgba(15,23,42,0.28)" };
const detailHeader = { background: "#fff", borderBottom: "1px solid #e2e8f0", padding: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 };
const agentIdentity = { display: "flex", alignItems: "center", gap: 14 };
const agentAvatar = { width: 42, height: 42, background: "#2563eb", color: "#fff", display: "grid", placeItems: "center", fontWeight: 900, fontSize: 18 };
const agentNameRow = { display: "flex", alignItems: "center", gap: 10 };
const detailName = { fontSize: 18 };
const agentMetaRow = { marginTop: 6, display: "flex", gap: 12, flexWrap: "wrap", color: "#64748b", fontSize: 12 };
const detailActions = { display: "flex", alignItems: "center", gap: 10 };
const plainAction = { minHeight: 36, border: 0, background: "transparent", color: "#334155", fontWeight: 800, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" };
const closeDetailButton = { width: 36, height: 36, border: 0, background: "transparent", color: "#475569", display: "grid", placeItems: "center", cursor: "pointer" };
const detailStats = { display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", background: "#fff", borderBottom: "1px solid #e2e8f0" };
const detailStat = { minHeight: 74, padding: "14px 10px", borderRight: "1px solid #e2e8f0", display: "grid", placeItems: "center", textAlign: "center", gap: 4 };
const detailStatValue = { fontSize: 18, fontWeight: 900 };
const detailTabs = { padding: "10px 16px", display: "flex", gap: 8, borderBottom: "1px solid #e2e8f0" };
const activeDetailTab = { minHeight: 34, padding: "0 14px", border: "1px solid #dbeafe", background: "#fff", color: "#2563eb", fontWeight: 900, display: "flex", alignItems: "center", gap: 7 };
const detailTab = { minHeight: 34, padding: "0 14px", border: 0, background: "transparent", color: "#475569", fontWeight: 800, display: "flex", alignItems: "center", gap: 7 };
const detailGrid = { padding: 22, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22 };
const detailCard = { background: "#fff", border: "1px solid #dbe4f0", padding: 20, display: "grid", gap: 14 };
const detailCardTitle = { margin: 0, fontSize: 15, fontWeight: 900 };
const commissionBox = { background: "#fff8e1", border: "1px solid #facc15", padding: 14, display: "grid", gap: 10 };
const commissionTiles = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 };
const miniMetric = { background: "#f8fafc", minHeight: 60, display: "grid", placeItems: "center", textAlign: "center", gap: 4, fontSize: 11, textTransform: "uppercase", color: "#64748b" };
const detailLine = { minHeight: 40, borderBottom: "1px solid #eef2f7", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, color: "#64748b", fontSize: 13 };

export default AdminAgents;
